import { useState, useRef, type FormEvent } from "react";
import { Alert, Button, FormField, Input, Modal, Select } from "@playbookai/ui";
import {
  createConnection,
  updateConnection,
  renameConnection,
  validateApiVersion,
  validateConnectionName,
  validateEndpointUrl,
  validateIdentityBinding,
  validateProviderConfigValue,
  validateSecretReference,
  type AdminConnection,
  type ConnectionInput,
  type ProviderDescriptor,
} from "../../../api/admin-ai-profiles-api.js";

export function ConnectionEditor({
  connection,
  providers,
  onClose,
  onSaved,
}: {
  connection: AdminConnection | null;
  providers: ProviderDescriptor[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [providerId, setProviderId] = useState(
    connection?.provider_key ?? providers[0]?.id ?? "",
  );
  const provider = providers.find((row) => row.id === providerId);
  /* Optional at runtime, not in the type: during a rolling deploy the client
     can be served by an AIS that predates this field. An empty list is not
     "no restriction" - it means the provider publishes no choice, so the field
     is hidden and no version is sent, which is what every provider accepts.
     `openai` refuses a non-null api_version outright. */
  const apiVersionChoices = provider?.api_versions ?? [];
  const [auth, setAuth] = useState(
    connection?.credential_binding.kind ?? provider?.auth_modes[0] ?? "api_key",
  );
  const [config, setConfig] = useState<Record<string, string>>(
    // Nullable registry fields are optional inputs; an API-created draft can
    // store null, which must not reach the submit path's string trimming.
    Object.fromEntries(
      Object.entries(connection?.provider_config ?? {}).filter(
        ([, value]) => value != null,
      ),
    ),
  );
  const [name, setName] = useState(connection?.display_name ?? "");
  const [endpoint, setEndpoint] = useState(connection?.endpoint_url ?? "");
  /* Empty, not "v1", when the provider publishes no versions: the field is then
     hidden, and openai refuses any non-null api_version. FormData used to
     contribute nothing for a field that did not render - controlled state has
     to reproduce that. Matches the provider-change handler below. */
  const [apiVersion, setApiVersion] = useState(
    connection?.api_version ?? provider?.api_versions?.[0] ?? "",
  );
  const [secret, setSecret] = useState(
    connection?.credential_binding.identity_binding_id ??
      connection?.credential_binding.secret_uri ??
      connection?.secret_uri ??
      "",
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const firstInput = useRef<HTMLInputElement>(null);
  const locked =
    connection?.mode === "embedding" || (connection?.locked ?? false);
  /* Which conditional fields are on screen. A field that is not rendered must
     not be able to block the submit, so the error map uses the same tests. */
  const showsEndpoint = Boolean(
    provider?.endpoint_required ||
    provider?.endpoint_any_host ||
    connection?.endpoint_url,
  );
  const showsApiVersion = Boolean(
    provider?.api_version_required || connection?.api_version,
  );
  const identityBound = auth === "workload_identity";
  const supportsGateway =
    providerId === "bedrock" &&
    Boolean(provider?.configuration_schema.properties.endpoint_type);
  const gateway = supportsGateway && config.endpoint_type === "custom_gateway";
  const endpointProvider =
    gateway && provider ? { ...provider, endpoint_required: true } : provider;
  const regionRequired = !gateway || auth !== "api_key";
  const requiredConfig = (key: string) =>
    supportsGateway && key === "region"
      ? regionRequired
      : (provider?.configuration_schema.required?.includes(key) ?? false);
  /* One list, used for both the error map and the rendering below, so a field
     cannot be shown without being validated. */
  const configFields = Object.entries(
    provider?.configuration_schema.properties ?? {},
  ).filter(
    ([key]) =>
      !["model_id", "protocol", "allowed_transports"].includes(key) &&
      !(supportsGateway && ["endpoint_type", "response_mode"].includes(key)),
  );
  /* An enum field renders as a Select that always holds a value, so only the
     free-text ones can be empty. */
  const configErrors: Record<string, string | null> = Object.fromEntries(
    configFields.map(([key, field]) => [
      key,
      locked || field.enum
        ? null
        : validateProviderConfigValue(
            config[key] ?? "",
            field.title ?? key,
            requiredConfig(key),
            /* The field carries the minLength/maxLength/pattern AIS published;
               without it the form validates presence and nothing else. */
            field,
          ),
    ]),
  );
  /* Recomputed every render from the draft, the way OrganizationAdmin does it,
     so every invalid field reports at once rather than one per submit. A
     locked connection accepts a rename and nothing else. */
  const errors = {
    name: validateConnectionName(name),
    endpoint:
      locked || !showsEndpoint
        ? null
        : validateEndpointUrl(endpoint, endpointProvider),
    apiVersion:
      locked || !showsApiVersion
        ? null
        : validateApiVersion(apiVersion, provider),
    secret: locked
      ? null
      : identityBound
        ? validateIdentityBinding(secret)
        : validateSecretReference(secret),
  };
  const invalid =
    Object.values(errors).some(Boolean) ||
    Object.values(configErrors).some(Boolean);
  /* A rule is only worth showing once the administrator has had a go at the
     field. Telling someone a blank form is wrong the moment it opens - or that
     an endpoint is required the moment choosing a provider reveals the field -
     reads as the product arguing with them. So a message waits for that field
     to be left, or for a save to be attempted, which is also how
     OrganizationAdmin holds its messages back until a draft exists. */
  /* Autofocus lands on the first control, so a blur is not on its own proof the
     administrator engaged with it - clicking straight past an untouched field
     would otherwise accuse it. A field speaks only once it has been EDITED and
     then left, or once a save is attempted. */
  const edited = useRef<Record<string, boolean>>({});
  const change = (field: string) => {
    edited.current[field] = true;
  };
  const leave = (field: string) => {
    if (edited.current[field])
      setTouched((prior) => ({ ...prior, [field]: true }));
  };
  const shown = (field: string, message: string | null) =>
    submitAttempted || touched[field] ? message : null;
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    /* Reveal everything at once rather than sending a request that cannot
       succeed. The button only starts refusing clicks after this, so it is
       never disabled while its reason is still hidden. */
    if (invalid) {
      setSubmitAttempted(true);
      return;
    }
    setSaving(true);
    setError(null);
    const value = (raw: string) => raw.trim();
    try {
      if (locked && connection) await renameConnection(connection, value(name));
      else {
        const secretValue = value(secret);
        const input: ConnectionInput = {
          display_name: value(name),
          provider_key: providerId,
          mode: "chat",
          secret_uri: identityBound ? "" : secretValue,
          credential_binding: identityBound
            ? { kind: auth, identity_binding_id: secretValue }
            : { kind: auth, secret_uri: secretValue },
          /* Trimmed BEFORE the filter, because the validator judges the
             trimmed value. Without this " my-project " passes the form and
             AIS refuses it on the published pattern - the page-level refusal
             this ticket exists to remove - and a whitespace-only value
             survives the `!== ""` test instead of being dropped. */
          provider_config: Object.fromEntries(
            Object.entries(config)
              .map(([key, v]) => [key, value(v)] as const)
              .filter(
                ([key, v]) =>
                  v !== "" && !["model_id", "protocol"].includes(key),
              ),
          ),
          endpoint_url: value(endpoint) || null,
          api_version: value(apiVersion) || null,
        };
        if (connection) {
          const { provider_key: _, mode: __, ...update } = input;
          await updateConnection(connection, update);
        } else await createConnection(input);
      }
      onSaved();
      onClose();
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Could not save connection.",
      );
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal
      open
      initialFocusRef={firstInput}
      onClose={() => {
        if (!saving) onClose();
      }}
      title={connection ? "Edit connection" : "Create connection"}
      size="md"
    >
      <form
        /* The app owns every message on this form. Without this, native
           validation fires first and the admin reads the browser's wording in
           the browser's language instead of ours. `required` and `type` stay
           for semantics and assistive tech. */
        noValidate
        onSubmit={(event) => {
          void submit(event);
        }}
        className="grid min-w-0 gap-4 [&>*]:min-w-0"
      >
        {error && (
          <Alert tone="critical" title="Couldn't save the connection">
            {error}
          </Alert>
        )}
        <FormField
          label="Connection name"
          hint="A name your admins can recognise, such as Production Claude."
          error={shown("name", errors.name)}
        >
          {/* No maxLength: it counts UTF-16 units, so it would cut a name
              short of the 160 code points AIS accepts. validateConnectionName
              counts code points instead. */}
          <Input
            name="name"
            required
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              change("name");
            }}
            onBlur={() => leave("name")}
            ref={firstInput}
          />
        </FormField>
        {locked && (
          <Alert tone="info">
            Credentials and endpoint are locked because this connection has
            served an enabled profile. Create a replacement to change them.
          </Alert>
        )}
        <fieldset disabled={locked} className="grid min-w-0 gap-4">
          <Select
            label="Provider"
            value={providerId}
            disabled={!!connection || saving}
            options={providers
              .filter((p) => p.modes.includes("chat"))
              .map((p) => ({ value: p.id, label: p.label }))}
            onChange={(value) => {
              const next = providers.find((p) => p.id === value);
              setProviderId(value);
              setConfig({});
              setEndpoint("");
              setApiVersion(next?.api_versions?.[0] ?? "");
              setAuth(next?.auth_modes[0] ?? "api_key");
            }}
          />
          {supportsGateway && (
            <Select
              label="Endpoint type"
              value={config.endpoint_type ?? "standard"}
              options={[
                { value: "standard", label: "Standard AWS" },
                { value: "custom_gateway", label: "Custom gateway" },
              ]}
              onChange={(value) => {
                const next: Record<string, string> = {
                  ...config,
                  endpoint_type: value,
                };
                if (value === "custom_gateway") {
                  next.response_mode = "invoke";
                  if (provider?.auth_modes.includes("api_key"))
                    setAuth("api_key");
                } else delete next.response_mode;
                setConfig(next);
              }}
            />
          )}
          {gateway && (
            <Select
              label="Gateway response mode"
              value={config.response_mode ?? "invoke"}
              options={[
                { value: "invoke", label: "Full response" },
                { value: "invoke_with_response_stream", label: "Streaming" },
              ]}
              onChange={(value) =>
                setConfig({ ...config, response_mode: value })
              }
            />
          )}
          {showsEndpoint && (
            <FormField
              label="Provider endpoint"
              hint={
                gateway
                  ? "The gateway's https base URL. Select its response mode above."
                  : providerId === "bedrock" && auth === "api_key"
                    ? "Optional AWS Bedrock runtime, FIPS or PrivateLink host in the selected region. For other hosts, select Custom gateway."
                    : provider?.endpoint_any_host && !provider.endpoint_required
                      ? "Optional. The host only, over https, with no path. Leave empty to use the provider's regional address."
                      : "The host only, over https, with no path."
              }
              error={shown("endpoint", errors.endpoint)}
            >
              <Input
                name="endpoint"
                type="url"
                required={endpointProvider?.endpoint_required}
                value={endpoint}
                onChange={(event) => {
                  setEndpoint(event.target.value);
                  change("endpoint");
                }}
                onBlur={() => leave("endpoint")}
              />
            </FormField>
          )}
          {(provider?.api_version_required || connection?.api_version) && (
            <FormField
              label="API version"
              hint={
                apiVersionChoices.length
                  ? "Only these versions reach this provider's chat API."
                  : "This provider publishes no list of versions, so this value is not checked here."
              }
              error={shown("apiVersion", errors.apiVersion)}
            >
              {/* A closed set served by AIS, so it is offered rather than
                  typed. Providers that publish no list keep free text - an
                  embedding deployment may legitimately use another version. */}
              {apiVersionChoices.length ? (
                <Select
                  value={apiVersion}
                  onChange={(value) => {
                    setApiVersion(value);
                    change("apiVersion");
                    leave("apiVersion");
                  }}
                  options={apiVersionChoices.map((value) => ({
                    value,
                    label: value,
                  }))}
                />
              ) : (
                <Input
                  name="apiVersion"
                  required={provider?.api_version_required}
                  value={apiVersion}
                  onChange={(event) => {
                    setApiVersion(event.target.value);
                    change("apiVersion");
                  }}
                  onBlur={() => leave("apiVersion")}
                />
              )}
            </FormField>
          )}
          {configFields.map(([key, field]) => (
            <FormField
              key={key}
              label={field.title ?? key}
              error={shown(`config.${key}`, configErrors[key] ?? null)}
            >
              {field.enum ? (
                <Select
                  value={config[key] ?? field.default ?? field.enum[0] ?? ""}
                  options={field.enum.map((value) => ({
                    value,
                    label: value,
                  }))}
                  onChange={(value) => setConfig({ ...config, [key]: value })}
                />
              ) : (
                <Input
                  value={config[key] ?? ""}
                  required={requiredConfig(key)}
                  onChange={(event) => {
                    setConfig({ ...config, [key]: event.target.value });
                    change(`config.${key}`);
                  }}
                  onBlur={() => leave(`config.${key}`)}
                />
              )}
            </FormField>
          ))}
          <Select
            label="Authentication"
            value={auth}
            onChange={setAuth}
            options={(provider?.auth_modes ?? []).map((value) => ({
              value,
              label:
                providerId === "bedrock" && value === "api_key"
                  ? "Bearer token"
                  : value.replaceAll("_", " "),
            }))}
          />
          <FormField
            label={
              identityBound ? "Operator identity binding" : "Secret reference"
            }
            hint={
              identityBound
                ? "The tenant-bound workload identity to authenticate as."
                : "Use your deployment's secret store: a versionless Azure Key Vault URI (https://your-vault.vault.azure.net/secrets/name) or a full AWS Secrets Manager secret ARN. An existing secret-reference JSON envelope is also accepted, including pinned versions. Enter a reference, never the secret value."
            }
            error={shown("secret", errors.secret)}
          >
            <Input
              name="secret"
              required
              value={secret}
              onChange={(event) => {
                setSecret(event.target.value);
                change("secret");
              }}
              onBlur={() => leave("secret")}
            />
          </FormField>
        </fieldset>
        <p className="text-sm text-pb-text-muted">
          Save this connection, then create profiles to select models and test
          their settings.
        </p>
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            disabled={saving}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={saving || !providerId || (submitAttempted && invalid)}
          >
            {saving ? "Saving…" : "Save connection"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
